import {
    BaseQueryFn,
    fetchBaseQuery,
    FetchBaseQueryMeta
} from "@reduxjs/toolkit/query/react";
import Cookies from "js-cookie";
import {authenticate, logOut} from "@/lib/slices/authSlice";
import {unregisterWebPushTokenLocally} from "@/lib/firebase/localToken";
import {ILoginResponse} from "@/lib/types/auth/ILoginResponse";
import {ApiTagsEnum} from "@/lib/enums/ApiTagsEnum";
import {ILimit} from "@/lib/types/wallet/ILimit";
import {IGroupedLimit} from "@/lib/types/wallet/IGroupedLimit";
import {IServerException} from "@/lib/types/common/IServerException";
import {CookiesKeysEnum} from "@/lib/enums/CookiesKeysEnum";

let refreshPromise: PromiseLike<{
    error: { status: number; data: unknown } | { status: "FETCH_ERROR"; data?: undefined; error: string } | {
        status: "PARSING_ERROR";
        originalStatus: number;
        data: string;
        error: string
    } | { status: "TIMEOUT_ERROR"; data?: undefined; error: string } | {
        status: "CUSTOM_ERROR";
        data?: unknown;
        error: string
    };
    data?: undefined;
    meta?: FetchBaseQueryMeta
} | { error?: undefined; data: unknown; meta?: FetchBaseQueryMeta }> | {
    error: { status: number; data: unknown } | { status: "FETCH_ERROR"; data?: undefined; error: string } | {
        status: "PARSING_ERROR";
        originalStatus: number;
        data: string;
        error: string
    } | { status: "TIMEOUT_ERROR"; data?: undefined; error: string } | {
        status: "CUSTOM_ERROR";
        data?: unknown;
        error: string
    };
    data?: undefined;
    meta?: FetchBaseQueryMeta
} | { error?: undefined; data: unknown; meta?: FetchBaseQueryMeta } | null = null;

const refreshTokenRequest = fetchBaseQuery({
    baseUrl: process.env.NEXT_PUBLIC_AUTH_API_URL,
});

const dynamicBaseQuery = (tag: ApiTagsEnum) => {
    const baseUrl =
        tag === ApiTagsEnum.AUTH
            ? process.env.NEXT_PUBLIC_AUTH_API_URL
            : tag === ApiTagsEnum.GAMES
                ? process.env.NEXT_PUBLIC_GAMES_API_URL
                : tag === ApiTagsEnum.WALLET
                    ? process.env.NEXT_PUBLIC_WALLET_API_URL
                    : process.env.NEXT_PUBLIC_AUTH_API_URL;

    return fetchBaseQuery({
        baseUrl,
        prepareHeaders,
    });
};

export const baseQueryWithRefresh = (tag: ApiTagsEnum): BaseQueryFn => {
    const rawBaseQuery = dynamicBaseQuery(tag);

    return async (args, api, extraOptions) => {
        let result = await rawBaseQuery(args, api, extraOptions);

        if (!result.error) {
            return result;
        }

        const serverError = (result.error as IServerException)?.data
        const code = serverError?.code
        const message = serverError?.message

        if (code === 423 && !message?.includes("Access denied for country")) {
            if (!window.location.pathname.includes("/banned")) {
                // Banned: stop this browser from receiving pushes (best-effort,
                // no backend DELETE — the session is dead).
                void unregisterWebPushTokenLocally();
                Cookies.set(CookiesKeysEnum.BANNED_USER_ID, message, {expires: 30});
                window.location.href = `/banned`;
            }
            return result;
        }

        if (result.error.status === 401) {
            if (!refreshPromise) {
                refreshPromise = refreshTokenRequest(
                    {
                        url: "/auth/refresh_token",
                        method: "POST",
                        body: {
                            deviceId: Cookies.get("deviceId"),
                            refreshToken: Cookies.get("refreshToken"),
                        }
                    },
                    api,
                    extraOptions
                );
            }

            try {
                const refreshResult = await refreshPromise;

                const response = refreshResult.data as ILoginResponse | undefined;
                const deviceId = Cookies.get("deviceId");

                if (response && deviceId) {
                    api.dispatch(authenticate({
                        accessToken: response.accessToken,
                        refreshToken: response.refreshToken,
                        deviceId
                    }))
                } else {
                    // Forced logout: refresh failed, Bearer is dead. Local FCM
                    // teardown only (backend send will get UNREGISTERED and
                    // auto-deactivate the row).
                    void unregisterWebPushTokenLocally();
                    api.dispatch(logOut());
                    return result;
                }

                result = await rawBaseQuery(args, api, extraOptions);
            } catch {
                void unregisterWebPushTokenLocally();
                api.dispatch(logOut());
            } finally {
                refreshPromise = null;
            }
        }

        attachRequestId(result);
        maybeShowSupportCode(result);
        return result;
    };
};

// unwrap() rejections carry only {status, data} — no meta — so the backend
// X-Request-Id is copied into error.data here, where component-level catch
// blocks can read it (see lib/api/supportCode.ts).
const attachRequestId = (result: {
    error?: { data?: unknown };
    meta?: FetchBaseQueryMeta;
}) => {
    if (!result.error) return;
    const requestId = result.meta?.response?.headers?.get("X-Request-Id");
    if (!requestId) return;
    if (typeof result.error.data === "object" && result.error.data !== null) {
        (result.error.data as Record<string, unknown>).requestId ??= requestId;
    }
};

// On server-side failures (5xx) surface the backend X-Request-Id as a support
// code. The header is exposed via CORS by every backend; network errors have
// no response and therefore no code to show.
const maybeShowSupportCode = (result: {
    error?: { status: number | string; originalStatus?: number };
    meta?: FetchBaseQueryMeta;
}) => {
    if (typeof window === "undefined" || !result.error) return;

    const status = result.error.status;
    const numericStatus = typeof status === "number" ? status : result.error.originalStatus;
    if (!numericStatus || numericStatus < 500) return;

    const requestId = result.meta?.response?.headers?.get("X-Request-Id");
    if (!requestId) return;

    void import("@/app/components/common/app-toast/showSupportCodeToast")
        .then(m => m.showSupportCodeToast(requestId));
};

export const prepareHeaders = (headers: Headers) => {
    const token = Cookies.get('accessToken');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
}

export const groupLimits = (response: ILimit[]) => {
    const grouped: IGroupedLimit[] = [];
    response.forEach(i => {
        const existing = grouped.find(v => v.currency === i.currencyCode);

        if (existing) {
            existing.networks.push({
                network: i.networkCode,
                minAmount: i.minAmount,
                maxAmount: i.maxAmount,
                isSupportedMemo: i.isSupportedMemo,
            })
        } else {
            grouped.push({
                currency: i.currencyCode,
                type: i.currencyType,
                networks: [
                    {
                        network: i.networkCode,
                        minAmount: i.minAmount,
                        maxAmount: i.maxAmount,
                        isSupportedMemo: i.isSupportedMemo,
                    }
                ]
            })
        }
    })
    return grouped;
}
